import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CompanyCreationForm from "./CompanyCreationForm";
import InvitedUserProfileForm from "./InvitedUserProfileForm";
import { useProfileSetup } from "@/hooks/useProfileSetup";

const ProfileSetupForm = () => {
  const {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    companyName,
    setCompanyName,
    companyRole,
    setCompanyRole,
    personalPhoneNumber,
    setPersonalPhoneNumber,
    website,
    setWebsite,
    streetAddress,
    setStreetAddress,
    suburb,
    setSuburb,
    city,
    setCity,
    provinceState,
    setProvinceState,
    postalCode,
    setPostalCode,
    setLogoUrl,
    setProfilePhotoUrl,
    isLoading,
    createNewCompany,
    setCreateNewCompany,
    handleSubmit,
    inviteToken,
    setInviteToken,
    isInvitedUser,
    invitedCompany,
  } = useProfileSetup();

  // If user is invited, show simplified profile form
  if (isInvitedUser && invitedCompany) {
    return (
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="text-2xl">Complete Your Profile</CardTitle>
          <CardDescription>
            Welcome! Complete your personal information to join {invitedCompany.name}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <InvitedUserProfileForm
            firstName={firstName}
            setFirstName={setFirstName}
            lastName={lastName}
            setLastName={setLastName}
            companyRole={companyRole}
            setCompanyRole={setCompanyRole}
            personalPhoneNumber={personalPhoneNumber}
            setPersonalPhoneNumber={setPersonalPhoneNumber}
            setProfilePhotoUrl={setProfilePhotoUrl}
            isLoading={isLoading}
            onSubmit={handleSubmit}
            companyName={invitedCompany.name}
          />
        </CardContent>
      </Card>
    );
  }

  // Default view for new users - show tabs for creating or joining company
  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle className="text-2xl">Complete Your Profile</CardTitle>
        <CardDescription>Add your personal and company information to get started</CardDescription>
      </CardHeader>
      <Tabs defaultValue="create" className="w-full" onValueChange={(value) => setCreateNewCompany(value === "create")}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="create">Create New Company</TabsTrigger>
          <TabsTrigger value="join">Join Existing Company</TabsTrigger>
        </TabsList>
        
        <TabsContent value="create">
          <CardContent>
            <CompanyCreationForm
              firstName={firstName}
              setFirstName={setFirstName}
              lastName={lastName}
              setLastName={setLastName}
              companyName={companyName}
              setCompanyName={setCompanyName}
              companyRole={companyRole}
              setCompanyRole={setCompanyRole}
              personalPhoneNumber={personalPhoneNumber}
              setPersonalPhoneNumber={setPersonalPhoneNumber}
              website={website}
              setWebsite={setWebsite}
              streetAddress={streetAddress}
              setStreetAddress={setStreetAddress}
              suburb={suburb}
              setSuburb={setSuburb}
              city={city}
              setCity={setCity}
              provinceState={provinceState}
              setProvinceState={setProvinceState}
              postalCode={postalCode}
              setPostalCode={setPostalCode}
              setLogoUrl={setLogoUrl}
              setProfilePhotoUrl={setProfilePhotoUrl}
              isLoading={isLoading}
              onSubmit={handleSubmit}
            />
          </CardContent>
        </TabsContent>
        
        <TabsContent value="join">
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label htmlFor="firstName" className="text-sm font-medium">First Name</label>
                  <input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="w-full p-2 border rounded-md"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="lastName" className="text-sm font-medium">Last Name</label>
                  <input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    className="w-full p-2 border rounded-md"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label htmlFor="personalPhoneNumber" className="text-sm font-medium">Personal Phone Number</label>
                <input
                  id="personalPhoneNumber"
                  type="tel"
                  value={personalPhoneNumber}
                  onChange={(e) => setPersonalPhoneNumber(e.target.value)}
                  className="w-full p-2 border rounded-md"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="inviteToken" className="text-sm font-medium">Invitation Token *</label>
                <input
                  id="inviteToken"
                  type="text"
                  value={inviteToken}
                  onChange={(e) => setInviteToken(e.target.value)}
                  required
                  placeholder="Enter your invitation token"
                  className="w-full p-2 border rounded-md"
                />
                <p className="text-sm text-muted-foreground">
                  Enter the invitation token you received to join an existing company.
                </p>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                {isLoading ? "Joining Company..." : "Complete Profile & Join Company"}
              </button>
            </form>
          </CardContent>
        </TabsContent>
      </Tabs>
    </Card>
  );
};

export default ProfileSetupForm;